import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Download, FileText, FileSpreadsheet, Loader2, CalendarDays } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import api, { formatApiError } from "@/lib/api";

const iso = (d) => format(d, "yyyy-MM-dd");
const presets = (today) => [
  ["This month", { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today }],
  ["Last month", { from: new Date(today.getFullYear(), today.getMonth() - 1, 1), to: new Date(today.getFullYear(), today.getMonth(), 0) }],
  ["Last 90 days", { from: new Date(today.getTime() - 89 * 86400000), to: today }],
  ["This year", { from: new Date(today.getFullYear(), 0, 1), to: today }],
];

export function StatementExport({ type }) {
  const today = new Date();
  const [range, setRange] = useState({ from: new Date(today.getFullYear(), today.getMonth(), 1), to: today });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const download = async (fmt) => {
    if (!range?.from) { toast.error("Pick a start date"); return; }
    setBusy(fmt);
    try {
      const r = await api.get("/statements/export", {
        params: { format: fmt, from: iso(range.from), to: iso(range.to || range.from), type }, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || `statement.${fmt}`;
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} statement downloaded`);
    } catch (e) {
      let msg = formatApiError(e);
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch {} }
      toast.error(msg);
    } finally { setBusy(null); }
  };

  const labelText = range?.from
    ? `${format(range.from, "MMM d, yyyy")} – ${range.to ? format(range.to, "MMM d, yyyy") : "…"}`
    : "Select a date range";

  return (
    <div className="glass rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid="statement-export">
      <div className="flex items-center gap-2 lg:mr-2">
        <Download className="w-5 h-5 text-cyan-400" />
        <div><h3 className="font-heading text-white text-sm">Download Statement</h3><p className="text-xs text-slate-500">Earnings, deposits & withdrawals for any period</p></div>
      </div>
      <div className="flex flex-wrap gap-3 items-center flex-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button data-testid="statement-range" className="glass glass-hover h-10 px-4 rounded-lg text-sm text-slate-200 inline-flex items-center gap-2 min-w-[240px]">
              <CalendarDays className="w-4 h-4 text-cyan-400" /> <span data-testid="statement-range-label">{labelText}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0 bg-[#0A0F1D]/95 backdrop-blur-xl border-cyan-500/20 text-slate-200" data-testid="statement-calendar">
            <div className="flex flex-col sm:flex-row">
              <div className="flex flex-row sm:flex-col gap-1 p-3 border-b sm:border-b-0 sm:border-r border-white/5 flex-wrap">
                {presets(today).map(([l, r]) => (
                  <button key={l} data-testid={`statement-preset-${l.toLowerCase().replace(/ /g, "-")}`} onClick={() => { setRange(r); setOpen(false); }}
                    className="text-left text-xs px-3 py-1.5 rounded-md text-slate-300 hover:bg-cyan-500/10 hover:text-cyan-200">{l}</button>
                ))}
              </div>
              <Calendar mode="range" selected={range} onSelect={(r) => setRange(r || {})} numberOfMonths={1} defaultMonth={range?.from} disabled={{ after: today }}
                classNames={{ day_selected: "bg-cyan-500 text-slate-950 hover:bg-cyan-400 hover:text-slate-950 focus:bg-cyan-500", day_range_middle: "aria-selected:bg-cyan-500/20 aria-selected:text-cyan-100", day_today: "border border-cyan-500/40" }} />
            </div>
          </PopoverContent>
        </Popover>
        <div className="flex gap-2">
          <button onClick={() => download("csv")} disabled={!!busy} data-testid="statement-csv" className="glass glass-hover h-10 px-4 rounded-lg text-sm text-cyan-300 inline-flex items-center gap-2 disabled:opacity-60">
            {busy === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} CSV
          </button>
          <button onClick={() => download("pdf")} disabled={!!busy} data-testid="statement-pdf" className="btn-glow h-10 px-4 rounded-lg text-sm font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
            {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
          </button>
        </div>
      </div>
    </div>
  );
}
